import { zodResolver } from "@hookform/resolvers/zod"
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import { InfoOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import { type ReactElement, useMemo } from "react"
import { useForm } from "react-hook-form"

import { BLANK_PURCHASE_ORDER_SHIP_TO } from "../../constants"
import { useGetStartPOData } from "../../hooks/useGetStartPOData"
import { useGetStartPurchaseOrderData } from "../../hooks/useGetStartPurchaseOrderData"
import type { PurchaseOrderDialogFormValues, PurchaseOrderDialogProps, PurchaseOrderShipToFormValue } from "../../types"
import { PurchaseOrderDialogSchema } from "../../types"
import { ProductsAdded } from "../ProductsAdded"

import { Button } from "@/components/common/Button"
import TextLineWithSpinner from "@/components/common/TextLineWithSpinner"
import { formatEnumValue } from "@/constants/pricingTiers"
import { ComputedProductStatus, type PricingPackage, type Product } from "@/graphql/codegen/graphql"
import { ShipToSelect } from "@/screens/Procurement/PurchaseOrder/components/ShipToSelect"
import { mapProductsPricingParams } from "@/utils/purchaseOrder"
import { formatLocationAddress } from "@/utils/utils"

export const PurchaseOrderDialog = ({
  isSubmitting,
  onClose,
  products,
  onSubmit,
  company,
}: PurchaseOrderDialogProps): ReactElement => {
  const {
    pricingResult: { orderLines, requestedDeliveryDate },
    pricingFetching,
  } = useGetStartPOData(mapProductsPricingParams(products))

  const {
    shipToLocations,
    shipToLocationListFetching,
    loadMore,
    freightTerms,
    incoterms,
    incotermsLocation,
    incotermsLocationsFetching,
    shippingViaCompanyId,
    isPricingFetching: isPricingListFetching,
  } = useGetStartPurchaseOrderData({
    selectedProducts: products,
    selectedCompany: company,
    pricingPackages: orderLines.map(({ pricingPackage }) => pricingPackage) as PricingPackage[],
  })

  const methods = useForm<PurchaseOrderDialogFormValues>({
    resolver: zodResolver(PurchaseOrderDialogSchema),
    defaultValues: {
      shipTo: BLANK_PURCHASE_ORDER_SHIP_TO,
    },
  })

  const handleFormSubmit = (data: PurchaseOrderDialogFormValues) => {
    const shipTo = JSON.parse(data.shipTo) as PurchaseOrderShipToFormValue
    onSubmit(
      {
        shipTo,
        freightTerms: freightTerms,
        deliveryTerms: incoterms, // TODO: Add Delivery Terms ; Currently blocked by Feature Rework & BE
        shipViaCompanyId: shippingViaCompanyId,
        deliveryLocationId: incotermsLocation?.locationId,
      },
      products,
      requestedDeliveryDate
    )
  }

  const hasInactiveProducts = useMemo(() => {
    return products.some((product) => product.computedProductStatus === ComputedProductStatus.Inactive)
  }, [products])

  return (
    <Dialog open={true} onClose={onClose} maxWidth='sm' fullWidth data-testid='start-po-dialog'>
      <DialogTitle className='px-[38px] font-normal text-primary'>Start Purchase Order</DialogTitle>
      {isSubmitting || pricingFetching ? (
        <CircularProgress size={48} className='mx-auto' />
      ) : (
        <>
          <DialogContent className='px-[38px]'>
            <Box className='space-y-4 rounded-md' data-testid='po-start-info'>
              <ProductsAdded products={products as unknown as Product[]} />

              {hasInactiveProducts && (
                <Alert severity='warning' icon={<WarningOutlined />} className='mb-4'>
                  <Box className='flex flex-col gap-1'>
                    <Typography variant='body2'>This Purchase Order Contains Inactive Products</Typography>
                  </Box>
                </Alert>
              )}
              <Box className='text-sm text-gray-600'>
                <Box component='p' className='flex h-8 items-center'>
                  <Box component='span' className='text-gray-900'>
                    Supplier:
                  </Box>
                  &nbsp;
                  <Box component='span' className='text-gray-700' data-testid='po-start-supplier-name'>
                    {company?.name || "-"}
                  </Box>
                </Box>
                <Box component='p' className='flex h-8 items-center'>
                  <Box component='span' className='text-gray-900'>
                    Ship From:
                  </Box>
                  &nbsp;
                  <Box component='span' className='text-gray-700' data-testid='po-start-ship-from'>
                    {products[0]?.locationsAssociations[0]?.shipping?.name || "-"}
                  </Box>
                </Box>
                <Box className='flex h-8 items-center'>
                  <Box component='span' className='text-gray-900'>
                    Incoterms:
                  </Box>
                  &nbsp;
                  <Box component='span' className='text-gray-700' data-testid='po-start-incoterms'>
                    <TextLineWithSpinner loading={isPricingListFetching}>{incoterms ?? "-"}</TextLineWithSpinner>
                  </Box>
                  <Tooltip title='More info about Incoterms' arrow>
                    <IconButton size='small' className='ml-1'>
                      <InfoOutlined fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box className='flex h-8 items-center'>
                  <Box component='span' className='text-gray-900'>
                    Freight Terms:
                  </Box>
                  &nbsp;
                  <Box component='span' className='text-gray-700' data-testid='po-start-freight-terms'>
                    <TextLineWithSpinner loading={isPricingListFetching}>
                      {freightTerms ? formatEnumValue(freightTerms) : "-"}
                    </TextLineWithSpinner>
                  </Box>
                  <Tooltip title='Terms can be changed later' arrow>
                    <IconButton size='small' className='ml-1'>
                      <InfoOutlined fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box className='flex h-8 items-center'>
                  <Box component='span' className='text-gray-900'>
                    Incoterms Location:
                  </Box>
                  &nbsp;
                  <Box component='span' className='text-gray-700' data-testid='po-start-incoterms-location'>
                    <TextLineWithSpinner loading={isPricingListFetching || incotermsLocationsFetching}>
                      {incotermsLocation ? formatLocationAddress(incotermsLocation) : "-"}
                    </TextLineWithSpinner>
                  </Box>
                </Box>
              </Box>
              <Divider />
              <Typography variant='body1' fontWeight={700} className='text-gray-700'>
                Shipping Information
              </Typography>
              <ShipToSelect
                control={methods.control}
                shipToLocations={shipToLocations}
                isFetching={shipToLocationListFetching}
                loadMore={loadMore}
              />
            </Box>
          </DialogContent>

          <DialogActions className='justify-between p-10'>
            <Button onClick={onClose} appearance='outlined' data-testid='cancel-button'>
              Cancel
            </Button>
            <Box className='flex items-center space-x-4'>
              <Button
                type='submit'
                data-testid='submit-button'
                onClick={methods.handleSubmit(handleFormSubmit)}
                variant='primary'
                disabled={!methods.formState.isValid || isSubmitting}
                endIcon={
                  isSubmitting ? (
                    <Box className='flex items-center'>
                      <CircularProgress size={16} className={isSubmitting ? "text-white disabled:text-primary" : ""} />
                      &nbsp;
                    </Box>
                  ) : null
                }
              >
                Continue
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
